import type { Surface } from './types'

// Shown when the current screen has no dedicated help surface.
export const DEFAULT_SURFACE: Surface = {
  key: 'getting-started',
  label: 'CounselPro',
  suggestions: [
    'How do I upload bank statements?',
    'What does reconciliation actually check?',
    'How does the analysis chat work?',
  ],
}

// Maps the current route to a help surface. `key` matches the article `surfaces`
// tags (a bare key like `insights` covers every `insights.*` article). Ordered
// most specific first; the first match wins.
interface SurfaceRoute {
  test: (pathname: string) => boolean
  surface: Surface
}

const SURFACE_ROUTES: SurfaceRoute[] = [
  {
    test: (path) => path.includes('/insights/duplicates'),
    surface: {
      key: 'insights.duplicates',
      label: 'Duplicates',
      suggestions: [
        'How does CounselPro find duplicate transactions?',
        'Why is a transaction flagged as a duplicate?',
        'Can the analysis chat review duplicates for me?',
      ],
    },
  },
  {
    test: (path) => path.includes('/insights/anomalies'),
    surface: {
      key: 'insights.anomalies',
      label: 'Anomalies',
      suggestions: [
        'What counts as an anomaly?',
        'How are anomalies detected?',
        'How do I act on a flagged anomaly?',
      ],
    },
  },
  {
    test: (path) => path.includes('/insights/categories'),
    surface: {
      key: 'insights.categories',
      label: 'Categories',
      suggestions: [
        'How are transactions categorized?',
        'Can I change a category?',
        'What do the spending categories mean?',
      ],
    },
  },
  {
    test: (path) => path.includes('/insights/income'),
    surface: {
      key: 'insights.income',
      label: 'Income',
      suggestions: [
        'How do I find unreported income?',
        'How is income identified across accounts?',
        'What does the income view show?',
      ],
    },
  },
  {
    test: (path) => path.includes('/insights/merchants'),
    surface: {
      key: 'insights.merchants',
      label: 'Merchants',
      suggestions: [
        'How are merchants identified?',
        'Why are some payments grouped together?',
        'What does the merchants view show?',
      ],
    },
  },
  {
    test: (path) => path.includes('/insights'),
    surface: {
      key: 'insights',
      label: 'Insights',
      suggestions: [
        'What can insights tell me?',
        'How do I read the coverage grid?',
        'How do I generate insights?',
      ],
    },
  },
  {
    test: (path) => path.includes('/analysis'),
    surface: {
      key: 'analysis.report',
      label: 'AI Analysis',
      suggestions: [
        'What goes into the AI analysis report?',
        'How do I generate an analysis report?',
        'Can I edit the report?',
      ],
    },
  },
  {
    test: (path) => path.includes('/checks'),
    surface: {
      key: 'checks',
      label: 'Checks',
      suggestions: [
        'How does CounselPro read checks?',
        'How do I review a check?',
        'Why is a check number wrong?',
      ],
    },
  },
  {
    test: (path) => path.includes('/workbench'),
    surface: {
      key: 'reconciliation',
      label: 'Reconciliation',
      suggestions: [
        'Why is this statement unverifiable?',
        "How do I fix a cycle that won't reconcile?",
        'Will my edits survive reprocessing?',
      ],
    },
  },
  {
    test: (path) => /\/projects\/[^/]+$/.test(path),
    surface: {
      key: 'projects.overview',
      label: 'Project overview',
      suggestions: [
        'What does the project overview show?',
        'How do I know when a project is ready?',
        'What needs my attention on a project?',
      ],
    },
  },
  {
    test: (path) => path.startsWith('/projects'),
    surface: {
      key: 'projects',
      label: 'Projects',
      suggestions: [
        'How do I create a project?',
        'How do I upload statements to a project?',
        'How do I organize projects into folders?',
      ],
    },
  },
  {
    test: (path) => path.startsWith('/settings'),
    surface: {
      key: 'settings',
      label: 'Settings',
      suggestions: [
        'How do I invite a teammate?',
        'How do I update my firm details?',
        'How do I manage my team?',
      ],
    },
  },
]

export function resolveSurface(pathname: string): Surface {
  return (
    SURFACE_ROUTES.find((route) => route.test(pathname))?.surface ??
    DEFAULT_SURFACE
  )
}

// An article belongs to a surface when its tags sit under the surface key (or the
// key sits under a tag), so a bare `insights` key matches `insights.duplicates`.
export function isArticleOnSurface(surfaces: string[], key: string): boolean {
  return surfaces.some(
    (surface) =>
      surface === key ||
      surface.startsWith(`${key}.`) ||
      key.startsWith(`${surface}.`),
  )
}
